/**
 * PublicGpReferral — v1.1 GP one-click inbound-referral form.
 * Public (no auth). Reached via /refer/{code} embedded in the outcomes-30day
 * email. Pre-fills with the clinic name + GP name, accepts patient details +
 * presenting complaint + urgency, posts to /api/gp/refer/{code}.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Send, Loader2, CheckCircle2, AlertCircle, Stethoscope } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import AppFooter from '../components/AppFooter';
import { t } from '../lib/i18n';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

export default function PublicGpReferral() {
  // Public page — no auth context, so we read the locale directly from
  // localStorage (the LocaleSwitcher persists it there) and default to 'en'.
  const locale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'en';
  const { code } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    patient_name: '', patient_email: '', patient_phone: '',
    presenting_complaint: '', urgency: 'this-week', discipline: 'physiotherapy', note: '',
  });

  useEffect(() => {
    setLoading(true); setErr('');
    axios.get(`${API}/gp/refer/${code}`)
      .then((r) => setMeta(r.data))
      .catch(() => setErr('This referral link is no longer valid.'))
      .finally(() => setLoading(false));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_name.trim()) return;
    setSending(true); setErr('');
    try {
      await axios.post(`${API}/gp/refer/${code}`, form);
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Submit failed. Please try again.');
    } finally { setSending(false); }
  };

  if (loading) {
    return <div className="p-8 text-center text-fg-muted">Loading…</div>;
  }
  if (err && !meta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center" data-testid="gp-refer-invalid">
        <AlertCircle className="w-10 h-10 text-[#B83A3A] mb-3"/>
        <h2 className="font-heading text-xl font-bold mb-1">{t(locale, 'referral_link_invalid')}</h2>
        <p className="text-sm text-fg-muted">{err}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg-secondary/30" data-testid="gp-refer-page">
      <header className="bg-bg-primary border-b border-soft py-4 px-6">
        <BrandLogo size={32}/>
      </header>
      <main className="flex-1 flex items-start justify-center p-4 md:p-8">
        <div className="w-full max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">{t(locale, 'refer_patient_to').toUpperCase()}</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="gp-refer-title">
            {t(locale, 'refer_patient_to')} {meta?.clinic_name || 'Physiogenic'}
          </h1>
          <p className="text-fg-muted text-sm mt-1 mb-6 flex items-center gap-2">
            <Stethoscope className="w-4 h-4"/> {t(locale, 'referrer')}:
            <span className="font-medium text-fg">{meta?.gp_name || 'GP'}</span>
            {meta?.gp_practice && <span className="text-fg-muted">· {meta.gp_practice}</span>}
          </p>

          {done ? (
            <div className="bento-card text-center p-8" data-testid="gp-refer-success">
              <CheckCircle2 className="w-12 h-12 text-[#1B5E20] mx-auto mb-3"/>
              <h2 className="font-heading text-xl font-bold mb-1">{t(locale, 'referral_received')}</h2>
              <p className="text-sm text-fg-muted">
                {t(locale, 'referral_received_body', { name: form.patient_name })}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="bento-card space-y-4" data-testid="gp-refer-form">
              <Field label={`${t(locale, 'patient_full_name')} *`}>
                <input className="input" required value={form.patient_name}
                       onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                       data-testid="gp-refer-patient-name"/>
              </Field>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label={t(locale, 'patient_email')}>
                  <input className="input" type="email" value={form.patient_email}
                         onChange={(e) => setForm({ ...form, patient_email: e.target.value })}
                         data-testid="gp-refer-patient-email"/>
                </Field>
                <Field label={t(locale, 'patient_phone')}>
                  <input className="input" value={form.patient_phone}
                         onChange={(e) => setForm({ ...form, patient_phone: e.target.value })}
                         data-testid="gp-refer-patient-phone"/>
                </Field>
              </div>
              <Field label={t(locale, 'presenting_complaint')}>
                <textarea className="input min-h-[80px]" value={form.presenting_complaint}
                          onChange={(e) => setForm({ ...form, presenting_complaint: e.target.value })}
                          placeholder="e.g. acute low-back pain after lifting, 3 days, radiating to left leg"
                          data-testid="gp-refer-complaint"/>
              </Field>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label={t(locale, 'urgency')}>
                  <select className="input" value={form.urgency}
                          onChange={(e) => setForm({ ...form, urgency: e.target.value })}
                          data-testid="gp-refer-urgency">
                    <option value="urgent">{t(locale, 'urgency_urgent')}</option>
                    <option value="this-week">{t(locale, 'urgency_standard')}</option>
                    <option value="flexible">{t(locale, 'urgency_flexible')}</option>
                  </select>
                </Field>
                <Field label={t(locale, 'discipline')}>
                  <select className="input" value={form.discipline}
                          onChange={(e) => setForm({ ...form, discipline: e.target.value })}
                          data-testid="gp-refer-discipline">
                    <option value="physiotherapy">Physiotherapy</option>
                    <option value="osteopathy">Osteopathy</option>
                    <option value="chiropractic">Chiropractic</option>
                    <option value="massage_therapy">Massage therapy</option>
                  </select>
                </Field>
              </div>
              <Field label={t(locale, 'note_optional')}>
                <textarea className="input min-h-[60px]" value={form.note}
                          onChange={(e) => setForm({ ...form, note: e.target.value })}
                          data-testid="gp-refer-note"/>
              </Field>
              {err && (
                <div className="text-sm text-[#B83A3A]" data-testid="gp-refer-err">{err}</div>
              )}
              <button type="submit" disabled={sending}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="gp-refer-submit">
                {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
                {t(locale, 'send_referral')}
              </button>
              <p className="text-[11px] text-fg-muted">
                We'll contact the patient on the next working day. Their data is stored under
                the clinic's GDPR / HIPAA-compliant records (EU-hosted). Referrer:
                <strong> {meta?.gp_name || 'GP'}</strong>.
              </p>
            </form>
          )}
        </div>
      </main>
      <AppFooter/>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}
